import { getContrastColor } from '@gettactic/helpers/src/colors/getContrastColor';
import Konva from 'konva';
import React from 'react';
import { Circle, Group, Rect, Text } from 'react-konva';
import { MapInteractiveZone, SpotShape } from '../../../../types';
import { useMapCanvas } from '../../contexts/MapCanvasContext';
import {
  DEFAULT_FONT_FAMILY,
  DEFAULT_NODE_CIRCLE_RADIUS,
  DEFAULT_NODE_LABEL_ALIGNMENT,
  DEFAULT_NODE_RECT_COLOR
} from '../../defines';
import { getAbsolutePositionOfShape } from '../../helpers/getAbsolutePositionOfShape';
import { getTextProps } from '../../helpers/getTextProps';
import { ShapeEditingHighlightOptions, SpotLayout } from '../../types';
import { useShape } from '../ShapeContext/useShape';
import { CircleSelectionNumber } from './CircleSelectionNumber';
import { InteractiveZone } from './InteractiveZone';

export interface SpotItemComponentProps {
  item: SpotShape;
  layout: SpotLayout;
  onSpotClick: (event: Konva.KonvaEventObject<PointerEvent>) => void;
  onShapeHighlight?: (highlight: ShapeEditingHighlightOptions) => void;
  isHighlighted?: boolean;
  interactiveZone?: MapInteractiveZone;
}

export const SpotItem = React.memo(
  ({
    item,
    layout,
    onSpotClick,
    interactiveZone,
    isHighlighted
  }: SpotItemComponentProps) => {
    const { isViewMode, highlightResource, setHoveringNode } = useMapCanvas();
    const { isHovered, isSelected, shape: _shape, selectIndex } = useShape();
    const shape = _shape as SpotShape;
    const { locked: isLocked } = shape;
    const rectColor = shape.color || DEFAULT_NODE_RECT_COLOR;

    const labelColor = React.useMemo(() => {
      return getContrastColor(rectColor);
    }, [rectColor]);

    const shapeStroke = isHighlighted
      ? 'red'
      : !isLocked && (isHovered || isSelected)
        ? '#000000'
        : 'transparent';

    const strokeWidth = isHighlighted ? 2 : 0.5;
    const spotType =
      item.spotAppearance ||
      shape.spotAppearance ||
      item.spotType ||
      shape.spotType;
    const labelPlacement = item.labelPlacement || shape.labelPlacement;

    const hasRect = spotType === 'desk' || spotType === 'meeting_room';

    let cornerRadius = item.rectProps?.cornerRadius || 0;
    if (spotType === 'meeting_room') {
      cornerRadius = 15;
    }

    const handleCircleMouseEnter = () => {
      if (shape.isInactive) return;
      const absPos = getAbsolutePositionOfShape(`${shape.id}-circle`);

      if (shape.resource_id && highlightResource)
        highlightResource(shape.resource_id, {
          x: absPos?.x || 0,
          y: absPos?.y || 0
        });

      setHoveringNode(shape);
    };

    const handleCircleMouseLeave = () => {
      if (shape.isInactive) return;
      if (shape.resource_id && highlightResource)
        highlightResource(null, { x: 0, y: 0 });

      setHoveringNode(null);
    };

    const circleRadius = item.circleProps?.radius || DEFAULT_NODE_CIRCLE_RADIUS;

    const textFontSize = shape.fontSize || 16;
    const textFontFamily = shape.fontFamily || DEFAULT_FONT_FAMILY;
    const textFontStyle = shape.textProps?.fontStyle || 'bold';
    const textRotation = item.textProps?.rotation ?? layout.text.rotation;
    const textColor =
      item.textProps?.color || (labelColor === 'light' ? 'white' : 'black');

    const shapeWidth = (item.textProps?.width ?? layout.text.width) || 0;

    return (
      <Group
        id={item.id}
        onMouseEnter={handleCircleMouseEnter}
        onMouseLeave={handleCircleMouseLeave}
        onClick={onSpotClick}
        onTap={onSpotClick}
      >
        <Group>
          {hasRect && (
            <Rect
              x={layout.rect.x}
              y={layout.rect.y}
              width={layout.rect.width}
              height={layout.rect.height}
              rotation={layout.rect.rotation}
              cornerRadius={cornerRadius}
              fill={rectColor}
              opacity={item.rectProps?.opacity || 1}
              stroke={shapeStroke}
              strokeWidth={strokeWidth}
              perfectDrawEnabled={false}
            />
          )}
          {labelPlacement !== 'hidden' && (
            <Text
              x={layout.text.x + (item.textProps?.marginLeft || 0)}
              y={
                layout.text.y +
                (item.textProps?.marginTop || 0) -
                (spotType === 'meeting_room'
                  ? textFontSize > 26
                    ? textFontSize / 2
                    : 0
                  : 0)
              }
              text={item.label}
              width={spotType === 'meeting_room' ? undefined : shapeWidth}
              fontStyle={textFontStyle}
              rotation={textRotation}
              perfectDrawEnabled={false}
              ref={(node) => {
                if (
                  node &&
                  (shape.orientation === 'top' ||
                    shape.orientation === 'bottom')
                ) {
                  const textWidth = node.width();
                  if (shape.textAlign === 'center') {
                    node.x((shapeWidth - textWidth) / 2);
                  }
                }
              }}
              {...getTextProps(shape, {
                defaultTextColor: textColor,
                defaultFontFamily: textFontFamily,
                defaultFontSize: textFontSize,
                defaultTextAlign: DEFAULT_NODE_LABEL_ALIGNMENT
              })}
            />
          )}
        </Group>
        <InteractiveZone
          interactiveZone={interactiveZone}
          shape={shape}
          layout={layout}
          onClick={() => null}
          selectIndex={selectIndex}
          isActive={!shape.isInactive}
        />
        {selectIndex === undefined && !isViewMode && (
          <Circle
            x={layout.circle.x}
            y={layout.circle.y}
            width={10}
            height={10}
            rotation={layout.rect.rotation}
            fill={item.resource_id ? '#60bb10' : 'orange'}
            opacity={0.5}
            perfectDrawEnabled={false}
          />
        )}
        {selectIndex !== undefined && (
          <CircleSelectionNumber
            layout={layout}
            label={String(selectIndex + 1)}
            radius={circleRadius}
            shape={shape}
          />
        )}
      </Group>
    );
  }
);
